import React, { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Skeleton, Typography } from '@barracuda-internal/bds-core-v2';
import { StatusDataItem } from 'src/types/detectionTypes';

interface StatusComponentV2Props {
	statusData: StatusDataItem[];
	loading: boolean;
	setTab: (tab: number) => void;
}

interface StatusCardProps {
	data: StatusDataItem;
	loading: boolean;
	handleClick: (data: StatusDataItem) => void;
}

const StatusComponentV2: React.FC<StatusComponentV2Props> = ({
	statusData,
	loading,
	setTab,
}) => {
	const navigate = useNavigate();

	function handleClick(data: StatusDataItem) {
		if (data.route == null) return;

		typeof data.route === 'number'
			? setTab(data.route)
			: navigate(data.route as string);
	}

	return (
		<Box sx={styles.container}>
			<Typography variant="body1" fontWeight={600} sx={styles.title}>
				What happened in the last 24 hours
			</Typography>
			<Box sx={styles.cardContainer}>
				{statusData.map((data, index) => (
					<StatusCard
						key={index}
						data={data}
						loading={loading}
						handleClick={handleClick}
					/>
				))}
			</Box>
		</Box>
	);
};

export default StatusComponentV2;

const StatusCard: React.FC<StatusCardProps> = ({
	data,
	loading,
	handleClick,
}) => {
	const isSuccess = data.status === 'success';

	return (
		<>
			<Box
				className="status-wrapper"
				sx={{
					cursor: data.route != null ? 'pointer' : 'default',
					minWidth: '150px',
					display: 'flex',
					flexDirection: 'column',
					height: '42px',
				}}
				role={data.route != null ? 'button' : undefined}
				onClick={() => handleClick(data)}
				tabIndex={data.route != null ? 0 : undefined}
				onKeyDown={
					data.route != null
						? (event) => {
								if (
									event.key === 'Enter' ||
									event.key === ' '
								) {
									event.preventDefault();
									handleClick(data);
								}
						  }
						: undefined
				}
			>
				<Box sx={styles.metricAlignment}>
					<Typography
						variant="h6"
						fontWeight={600}
						sx={styles.metricText}
					>
						{loading ? (
							<Skeleton variant="text" width={80} height={25} />
						) : isSuccess ? (
							formatNumber(data.count)
						) : (
							'--'
						)}
					</Typography>

					{data.type === 'activePolicies' && isSuccess && (
						<Typography variant="body1" sx={styles.metricSubtext}>
							&nbsp;{`of ${data.totalCount}`}
						</Typography>
					)}

					{data.type === 'activeDetections' &&
						data.detectionsbeforeDateCount !== 0 &&
						isSuccess && (
							<Typography
								variant="body1"
								sx={styles.metricSubtext}
							>
								&nbsp;
								{`(${convertToPercentage(
									data.count,
									data.detectionsbeforeDateCount,
								)}% from yesterday)`}
							</Typography>
						)}
				</Box>

				<Typography variant="body1" sx={styles.subtitleText}>
					{loading ? (
						<Skeleton variant="text" width={140} height={16} />
					) : (
						data.title
					)}
				</Typography>
			</Box>
		</>
	);
};

function formatNumber(num: number): string {
	if (num < 0) {
		return '--';
	} else if (num >= 1000000) {
		return (num / 1000000).toFixed(1).replace(/\.0$/, '') + ' M';
	} else if (num >= 1000) {
		return (num / 1000).toFixed(1).replace(/\.0$/, '') + ' K';
	} else {
		return num.toString();
	}
}

function convertToPercentage(newVal: number, oldVal: number): string {
	const percentage = ((newVal - oldVal) / oldVal) * 100;

	if (percentage % 1 === 0) {
		return percentage > 0 ? `+${percentage}` : `${percentage}`;
	} else {
		return percentage > 0
			? `+${percentage.toFixed(1)}`
			: `${percentage.toFixed(1)}`;
	}
}

const styles: { [key: string]: CSSProperties } = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		border: '1px solid #f0f0ef',
		marginRight: '24px',
		padding: '5px 0px 25px 0px',
	},
	title: {
		paddingLeft: '20px',
		margin: '12px 0px',
	},
	cardContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '30px',
		padding: '0px 20px',
		justifyContent: 'space-between',
	},
	metricAlignment: {
		display: 'inline-flex',
		alignItems: 'baseline',
	},
	metricText: {
		margin: 0,
		lineHeight: '1.2',
	},
	metricSubtext: {
		verticalAlign: 'baseline',
	},
	subtitleText: {
		margin: 0,
		lineHeight: '1.2',
	},
	margin0: {
		margin: 0,
	},
};
